import React, { useState } from "react";
import { Bike, CheckCircle2, Package, Pencil } from "lucide-react";
import { EntregadorEmEntrega } from "../types";

interface EntregadoresEmEntregaProps {
  entregadores: EntregadorEmEntrega[];
  onAtualizarPedidos?: (
    entregaId: number,
    pedidosDisplayIds: number[]
  ) => Promise<void>;
  onFinalizarEntrega?: (entregaId: number) => Promise<void>;
  finalizandoEntregaId?: number | null;
}

const parseNumerosPedidos = (value: string): number[] => {
  return value
    .split(/[\s,;]+/)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);
};

const EntregadoresEmEntrega: React.FC<EntregadoresEmEntregaProps> = ({
  entregadores,
  onAtualizarPedidos,
  onFinalizarEntrega,
  finalizandoEntregaId = null,
}) => {
  const [editandoEntrega, setEditandoEntrega] =
    useState<EntregadorEmEntrega | null>(null);
  const [inputNumeros, setInputNumeros] = useState("");
  const [salvando, setSalvando] = useState(false);

  const abrirEdicao = (entregador: EntregadorEmEntrega) => {
    setEditandoEntrega(entregador);
    setInputNumeros(
      (entregador.pedidosDisplayIds ?? []).length > 0
        ? entregador.pedidosDisplayIds!.join(", ")
        : ""
    );
  };

  const fecharEdicao = () => {
    setEditandoEntrega(null);
    setInputNumeros("");
  };

  const handleSalvar = async () => {
    if (!editandoEntrega || !onAtualizarPedidos) return;
    const ids = parseNumerosPedidos(inputNumeros);
    setSalvando(true);
    try {
      await onAtualizarPedidos(editandoEntrega.id, ids);
      fecharEdicao();
    } catch {
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-light">
            <Bike size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-neutral-900">
              Entregadores em Entrega
            </h3>
            <p className="text-sm text-neutral-500">
              {entregadores.length} entregador
              {entregadores.length !== 1 ? "es" : ""} ativo
              {entregadores.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </div>

      {entregadores.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-neutral-200 bg-neutral-50 p-12 text-center">
          <Bike size={48} className="mx-auto mb-4 text-neutral-400" />
          <p className="text-lg font-medium text-neutral-500">
            Nenhum entregador em entrega
          </p>
          <p className="mt-1 text-sm text-neutral-400">
            Os entregadores aparecerão aqui quando iniciarem uma entrega
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {entregadores.map((entregador) => (
            <div
              key={entregador.id}
              className="flex items-center justify-between rounded-lg border-2 border-success/30 bg-success-light/5 p-4 transition-all hover:border-success/50 hover:shadow-sm"
            >
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-success-light">
                  <Bike size={20} className="text-success" />
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-900">
                    {entregador.nome} {entregador.sobrenome}
                  </h4>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="flex items-center gap-1 text-xs font-medium text-success-text">
                      <CheckCircle2 size={14} />
                      Em entrega
                    </span>
                    <span className="flex items-center gap-1 text-xs text-neutral-500">
                      <Package size={12} />
                      {entregador.quantidadeEntregasFinalizadas}{" "}
                      {entregador.quantidadeEntregasFinalizadas === 1
                        ? "entrega finalizada"
                        : "entregas finalizadas"}
                    </span>
                    <span className="text-xs text-neutral-600">
                      {entregador.quantidadeEnderecos === 1
                        ? "1 endereço"
                        : `${entregador.quantidadeEnderecos} endereços`}
                    </span>
                    {(entregador.pedidosDisplayIds ?? []).length > 0 && (
                      <span className="text-xs text-neutral-600">
                        Pedidos: #
                        {(entregador.pedidosDisplayIds ?? []).join(", #")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {onAtualizarPedidos || onFinalizarEntrega ? (
                <div className="flex shrink-0 items-center gap-2">
                  {onFinalizarEntrega && (
                    <button
                      type="button"
                      onClick={() => void onFinalizarEntrega(entregador.id)}
                      disabled={finalizandoEntregaId === entregador.id}
                      className="flex items-center gap-1.5 rounded-lg bg-success px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-success-dark disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Finalizar entrega"
                    >
                      <CheckCircle2 size={16} />
                      {finalizandoEntregaId === entregador.id
                        ? "Finalizando…"
                        : "Finalizar"}
                    </button>
                  )}
                  {onAtualizarPedidos && (
                    <button
                      type="button"
                      onClick={() => abrirEdicao(entregador)}
                      disabled={finalizandoEntregaId === entregador.id}
                      className="flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Editar números do pedido"
                    >
                      <Pencil size={16} />
                      Editar
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {editandoEntrega && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-editar-pedidos-title"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <h2
              id="modal-editar-pedidos-title"
              className="text-lg font-bold text-neutral-900"
            >
              Editar números do pedido
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              {editandoEntrega.nome} {editandoEntrega.sobrenome}
            </p>
            <label className="mt-4 block text-sm font-medium text-neutral-700">
              Números dos pedidos (separados por vírgula ou espaço)
            </label>
            <input
              type="text"
              value={inputNumeros}
              onChange={(e) => setInputNumeros(e.target.value)}
              placeholder="Ex: 123, 456 ou 123 456"
              className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-neutral-900 placeholder-neutral-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={fecharEdicao}
                disabled={salvando}
                className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSalvar}
                disabled={salvando}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {salvando ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntregadoresEmEntrega;
